export type ColorScheme = 'light' | 'dark';

export type HostThemeColors = {
  /** Primary brand color shared with embedded services. */
  primary: string;
  /** Global application background color. */
  appBackground: string;
  /** Main surface background color. */
  surface: string;
  /** Secondary surface background color. */
  surfaceMuted: string;
  /** High-emphasis text color. */
  textPrimary: string;
  /** Secondary text color. */
  textSecondary: string;
  /** Default border color. */
  border: string;
  /** Overlay background used on loading and modal layers. */
  overlay: string;
  /** Workspace canvas background color. */
  canvasBackground: string;
  /** Workspace canvas grid color. */
  canvasGrid: string;
  /** Inactive breadcrumb color used across the canvas. */
  breadcrumbInactive: string;
};

export type HostTheme = {
  /** Active light or dark color scheme. */
  colorScheme: ColorScheme;
  /** Semantic colors exposed to embedded services. */
  colors: HostThemeColors;
};

export type ThemeSettings = {
  /** Persisted color scheme selected by the user. */
  colorScheme: ColorScheme;
};

export const themeSettingsStorageKey = 'themeSettings';

export const lightHostTheme: HostTheme = {
  colorScheme: 'light',
  colors: {
    primary: '#4E3EFF',
    appBackground: '#F6F6F6',
    surface: '#FFFFFF',
    surfaceMuted: '#E7E7E7',
    textPrimary: '#18212D',
    textSecondary: '#6C757D',
    border: '#D9D9D9',
    overlay: 'rgba(255, 255, 255, 0.72)',
    canvasBackground: '#FFFFFF',
    canvasGrid: '#0000000F',
    breadcrumbInactive: '#6C757D',
  },
};

export const darkHostTheme: HostTheme = {
  colorScheme: 'dark',
  colors: {
    primary: '#7C70FF',
    appBackground: '#12161D',
    surface: '#1B2230',
    surfaceMuted: '#222B3B',
    textPrimary: '#E6EDF7',
    textSecondary: '#A5B1BB',
    border: '#3A455A',
    overlay: 'rgba(16, 22, 31, 0.72)',
    canvasBackground: '#10141C',
    canvasGrid: '#FFFFFF12',
    breadcrumbInactive: '#A5B1BB',
  },
};

/**
 * Resolves the preferred color scheme from the current browser environment.
 * @returns The system color scheme when available, otherwise `light`.
 */
export const getSystemColorScheme = (): ColorScheme => {
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }

  return 'light';
};

/**
 * Returns the host theme payload that is passed to embedded service UIs.
 * @param colorScheme The active application color scheme.
 * @returns A semantic host theme payload.
 */
export const getHostTheme = (colorScheme: ColorScheme): HostTheme => {
  return colorScheme === 'dark' ? darkHostTheme : lightHostTheme;
};
